import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import { 
  UploadFileToNetsuite,
  GetRegistrationFiles,
  SubmitFilesUploadedReReg,
  GetReRegistrationFileDownloads,
} from "../../../../services/ReRegistrationService";
import { validateFile } from "../utils/fileHelpers";

export const useSupportingDocuments = (regId, onSubmitSuccess) => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadingIds, setUploadingIds] = useState(new Set());
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [hasJustUploaded, setHasJustUploaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uploadCountBeforeFetch = useRef(0);

  // Reset state when regId changes
  useEffect(() => {
    if (regId && regId > 0) {
      setUploadedFiles({});
      setInitialLoadComplete(false);
      uploadCountBeforeFetch.current = 0;
    }
  }, [regId]);

  useEffect(() => {
    if (!regId || regId <= 0) return;
    
    let isMounted = true;
    
    GetRegistrationFiles(regId)
      .then((res) => {
        if (!isMounted || !res?.data) return;
        
        const uploadedMap = {};
        let uploadedCount = 0;
        res.data.forEach((file) => {
          if (file.uploaded === 1) {
            uploadedCount++;
            uploadedMap[file.id] = {
              uploaded: true,
              filePath: file.file_path,
              fileName: file.file_name || (file.file_path ? file.file_path.split('/').pop() : null),
            };
          }
        });
        
        // Detect if a new file was uploaded by comparing counts
        if (initialLoadComplete && uploadedCount > uploadCountBeforeFetch.current) {
          setHasJustUploaded(true);
        }
        
        setUploadedFiles(uploadedMap);
        uploadCountBeforeFetch.current = uploadedCount;
        setInitialLoadComplete(true);
      })
      .catch((error) => {
        console.error("Error fetching registration files:", error);
        setInitialLoadComplete(true);
      });
    
    return () => { isMounted = false; };
  }, [regId, fetchTrigger]);

  const handleFileUpload = useCallback((fileConfig, selectedFile, setReloadFiles) => {
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      Swal.fire({ icon: "error", title: "Invalid File", text: validation.message });
      return;
    }

    const recordId = fileConfig.value;
    setUploadingIds(prev => new Set(prev).add(recordId));

    const formData = new FormData();
    formData.append("doc", selectedFile);
    formData.append("id", recordId);

    Swal.fire({
      title: "File Uploading....",
      onOpen: function () {
        Swal.showLoading();
      },
    });

    UploadFileToNetsuite(formData)
      .then((res) => {
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Updated successfully",
        }).then(() => {
          setFetchTrigger(t => t + 1);
        });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "An error occurred during upload",
        }).then(() => {
          window.location.reload();
        });
      })
      .finally(() => {
        setUploadingIds(prev => {
          const next = new Set(prev);
          next.delete(recordId);
          return next;
        });
        setReloadFiles && setReloadFiles(true);
      });
  }, []);

  return {
    uploadedFiles,
    uploadingIds,
    handleFileUpload,
    initialLoadComplete,
    hasJustUploaded,
    setHasJustUploaded,
    isSubmitting,
    setIsSubmitting
  };
};

export const useFileDownloads = () => {
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    GetReRegistrationFileDownloads()
      .then((res) => {
        if (!isMounted || !res?.data?.success) return;

        // Map API response to expected format
        const mappedDownloads = (res.data.files || []).map((file, index) => ({
          value: index + 1,
          label: file.name,
          description: file.caption,
          url: file.path,
          disabled: !file.path || file.path === "#",
        }));

        setDownloadFiles(mappedDownloads);
      })
      .catch((error) => {
        console.error("Error fetching file downloads:", error);
        setDownloadFiles([]);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return { downloadFiles, loading };
};

